/**
 * useOnboarding Hook
 * Manages onboarding state, progress tracking, and persistence
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type { OnboardingProgress } from '@/types/onboarding';

const ONBOARDING_STORAGE_KEY = 'legalsyncup_onboarding_progress';
const FIRST_LOGIN_STORAGE_KEY = 'legalsyncup_first_login';

/**
 * Default onboarding progress state
 */
const defaultProgress: OnboardingProgress = {
  completedSteps: [],
  completedTours: [],
  completedChecklistItems: [],
  skippedTours: [],
  hasSeenWelcome: false,
  onboardingComplete: false,
};

/**
 * Load onboarding progress from localStorage
 */
const loadProgress = (): OnboardingProgress => {
  try {
    const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...defaultProgress, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load onboarding progress:', error);
  }
  return defaultProgress;
};

/**
 * Save onboarding progress to localStorage
 */
const saveProgress = (progress: OnboardingProgress): void => {
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error('Failed to save onboarding progress:', error);
  }
};

/**
 * Check if this is a new user (first login)
 */
const isNewUser = (): boolean => {
  try {
    const firstLogin = localStorage.getItem(FIRST_LOGIN_STORAGE_KEY);
    if (!firstLogin) {
      // Mark as first login
      localStorage.setItem(FIRST_LOGIN_STORAGE_KEY, new Date().toISOString());
      return true;
    }

    // Check if first login was recent (within 7 days)
    const firstLoginDate = new Date(firstLogin);
    const daysSinceFirstLogin = (Date.now() - firstLoginDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceFirstLogin <= 7;
  } catch (error) {
    console.error('Failed to check first login status:', error);
    return false;
  }
};

/**
 * useOnboarding Hook
 * Main hook for managing onboarding state
 */
export const useOnboarding = () => {
  const [progress, setProgress] = useState<OnboardingProgress>(loadProgress);

  // Sync progress to localStorage whenever it changes
  useEffect(() => {
    saveProgress(progress);
  }, [progress]);

  /**
   * Mark a step as completed
   */
  const completeStep = useCallback((stepId: string) => {
    setProgress((prev) => {
      if (prev.completedSteps.includes(stepId)) {
        return prev;
      }
      return {
        ...prev,
        completedSteps: [...prev.completedSteps, stepId],
        lastActiveStep: stepId,
      };
    });
  }, []);

  /**
   * Mark a tour as completed
   */
  const completeTour = useCallback((tourId: string) => {
    setProgress((prev) => {
      if (prev.completedTours.includes(tourId)) {
        return prev;
      }
      return {
        ...prev,
        completedTours: [...prev.completedTours, tourId],
        lastActiveTour: tourId,
      };
    });
  }, []);

  /**
   * Skip a tour (mark as not interested)
   */
  const skipTour = useCallback((tourId: string) => {
    setProgress((prev) => {
      if (prev.skippedTours.includes(tourId)) {
        return prev;
      }
      return {
        ...prev,
        skippedTours: [...prev.skippedTours, tourId],
      };
    });
  }, []);

  /**
   * Mark a checklist item as completed
   */
  const completeChecklistItem = useCallback((itemId: string) => {
    setProgress((prev) => {
      if (prev.completedChecklistItems.includes(itemId)) {
        return prev;
      }
      return {
        ...prev,
        completedChecklistItems: [...prev.completedChecklistItems, itemId],
      };
    });
  }, []);

  /**
   * Mark a checklist item as uncompleted
   */
  const uncompleteChecklistItem = useCallback((itemId: string) => {
    setProgress((prev) => ({
      ...prev,
      completedChecklistItems: prev.completedChecklistItems.filter((id) => id !== itemId),
    }));
  }, []);

  /**
   * Check if a checklist item is completed
   */
  const isChecklistItemCompleted = useCallback(
    (itemId: string): boolean => {
      return progress.completedChecklistItems.includes(itemId);
    },
    [progress.completedChecklistItems]
  );

  /**
   * Dismiss welcome modal
   */
  const dismissWelcome = useCallback(() => {
    setProgress((prev) => ({
      ...prev,
      hasSeenWelcome: true,
    }));
  }, []);

  /**
   * Mark entire onboarding as complete
   */
  const setOnboardingComplete = useCallback(() => {
    setProgress((prev) => ({
      ...prev,
      onboardingComplete: true,
    }));
  }, []);

  /**
   * Reset all onboarding progress (for testing or replay)
   */
  const resetOnboarding = useCallback(() => {
    setProgress(defaultProgress);
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  }, []);

  /**
   * Check if a tour is completed
   */
  const isTourCompleted = useCallback(
    (tourId: string): boolean => {
      return progress.completedTours.includes(tourId);
    },
    [progress.completedTours]
  );

  /**
   * Check if a step is completed
   */
  const isStepCompleted = useCallback(
    (stepId: string): boolean => {
      return progress.completedSteps.includes(stepId);
    },
    [progress.completedSteps]
  );

  /**
   * Determine if onboarding should be shown
   */
  const shouldShowOnboarding = useCallback((): boolean => {
    // Don't show if explicitly marked as complete
    if (progress.onboardingComplete) {
      return false;
    }

    // Show for new users who haven't seen welcome
    if (isNewUser() && !progress.hasSeenWelcome) {
      return true;
    }

    // Show if there are incomplete tours that haven't been skipped
    return progress.completedTours.length === 0 && progress.skippedTours.length === 0;
  }, [progress]);

  /**
   * Calculate overall progress percentage
   */
  const overallProgress = useMemo(() => {
    const totalItems = 5; // Number of main checklist items
    const completedItems = progress.completedChecklistItems.length;
    return Math.round((completedItems / totalItems) * 100);
  }, [progress.completedChecklistItems]);

  return {
    progress,
    completeStep,
    completeTour,
    skipTour,
    completeChecklistItem,
    uncompleteChecklistItem,
    isChecklistItemCompleted,
    dismissWelcome,
    setOnboardingComplete,
    resetOnboarding,
    isTourCompleted,
    isStepCompleted,
    shouldShowOnboarding,
    isNewUser: isNewUser(),
    overallProgress,
  };
};
